import { useCallback, useEffect, useRef, useState } from 'react';

type UseTruncateOptions = {
    dependency?: string;
};

// `scrollWidth` is the element's full content width regardless of overflow; `clientWidth` is what
// its own box actually shows. The gap between them is exactly what `truncate` clips.
export function useTruncate<T extends HTMLElement>({ dependency = '' }: UseTruncateOptions = {}) {
    const ref = useRef<T>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = useCallback(() => {
        const element = ref.current;
        if (!element) return;
        setIsTruncated(element.scrollWidth > element.clientWidth);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: dependency re-triggers remeasurement
    useEffect(() => {
        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [checkTruncation, dependency]);

    return { ref, isTruncated };
}
